/**
 * Copyright (C) 2025 GIP-RECIA, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import type { TemplateResult } from 'lit'
import type {
  FavoriteSection,
  ServiceAndServiceResource,
  UpdatedFavoriteSection,
} from '../../../types/index.ts'
import {
  faArrowLeft,
  faArrowRight,
  faFloppyDisk,
  faGear,
  faTimes,
} from '@fortawesome/free-solid-svg-icons'
import { localized, msg, str } from '@lit/localize'
import { componentName } from 'common/config.ts'
import { css, html, LitElement, nothing, unsafeCSS } from 'lit'
import { property, state } from 'lit/decorators.js'
import { map } from 'lit/directives/map.js'
import { range } from 'lit/directives/range.js'
import { repeat } from 'lit/directives/repeat.js'
import langHelper from '../../../helpers/langHelper.ts'
import { getCategory } from '../../../utils/categoryUtils.ts'
import { getIcon } from '../../../utils/fontawesomeUtils.ts'
import { getSvgIconService } from '../../../utils/iconUtils.ts'
import { setLocale } from '../../../utils/localizationUtils.ts'
import styles from './style.scss?inline'

@localized()
export class ReciaFavoriteLayout extends LitElement {
  @property({ type: Array })
  data?: FavoriteSection[]

  @property({ type: Boolean })
  loading: boolean = false

  @property({ type: Number, attribute: 'loading-sections' })
  loadingSections?: number

  @property({ type: Number, attribute: 'loading-sections-items' })
  loadingSectionsItems?: number

  @state()
  tmpData?: FavoriteSection[]

  @state()
  isManage: boolean = false

  constructor() {
    super()
    const lang = langHelper.getPageLang()
    setLocale(lang)
    langHelper.setLocale(lang)
  }

  connectedCallback(): void {
    super.connectedCallback()
    this.addEventListener('reset', this.reset.bind(this))
  }

  disconnectedCallback(): void {
    super.disconnectedCallback()
    this.removeEventListener('reset', this.reset.bind(this))
  }

  reset(_: Event) {
    this.isManage = false
    this.tmpData = undefined
  }

  toggleManage(save: boolean = false): void {
    const delay = save ? 300 : 0

    if (!this.isManage) {
      this.tmpData = this.data ? [...this.data] : undefined
    }
    else {
      if (save)
        this.dispatchEvent(new CustomEvent('updated', { detail: { newValue: this.getDiffs() } }))
      setTimeout(() => {
        this.tmpData = undefined
      }, delay)
    }
    setTimeout(() => {
      this.isManage = !this.isManage
    }, delay)
  }

  getDiffs(): UpdatedFavoriteSection[] | undefined {
    if (!this.data || !this.tmpData)
      return undefined

    const changes: UpdatedFavoriteSection[] = []

    this.tmpData.forEach((newData) => {
      const oldData = this.data?.find(({ id }) => id === newData.id)

      const deleted = oldData?.items.filter(item => !newData.items.includes(item)) ?? []
      const oldDataItemsNoDeleted = oldData?.items.filter(item => newData.items.includes(item))
      const orderHasChanged = oldDataItemsNoDeleted?.some((item, index) => {
        return newData.items.findIndex(({ id }) => id === item.id) !== index
      }) ?? false

      changes.push({
        ...newData,
        deleted,
        orderHasChanged,
      })
    })

    return changes
  }

  deleteItem(sectionId: string, item: ServiceAndServiceResource, index: number): void {
    this.tmpData = this.tmpData!.map((section) => {
      if (section.id !== sectionId)
        return section

      const items = section.items.filter(el => el !== item)

      return { ...section, items }
    })

    const sectionItems = this.tmpData!.find(({ id }) => sectionId === id)?.items
    if (!sectionItems || sectionItems.length === 0) {
      this.shadowRoot?.querySelector(`#favorite-section-${sectionId}-empty`)?.focus()
    }
    else {
      const newIndex = index <= sectionItems.length - 1 ? index : index - 1
      this.shadowRoot?.querySelector(
        `#favorite-section-${sectionId}-item-${sectionItems[newIndex].id} .action-delete > button`,
      )?.focus()
    }
  }

  moveItem(sectionId: string, item: ServiceAndServiceResource, newPosition: '-1' | '+1'): void {
    this.tmpData = this.tmpData!.map((section) => {
      if (section.id !== sectionId)
        return section

      const index = section.items.indexOf(item)
      if (index === -1)
        return section

      const newIndex = index + Number(newPosition)

      if (newIndex < 0 || newIndex >= section.items.length)
        return section

      const items = [...section.items]

      const [movedItem] = items.splice(index, 1)
      items.splice(newIndex, 0, movedItem)

      return { ...section, items }
    })

    const sectionItems = this.tmpData!.find(({ id }) => sectionId === id)?.items
    const action = sectionItems?.[0]?.id === item.id
      ? 'next'
      : sectionItems?.slice(-1)[0]?.id === item.id
        ? 'back'
        : newPosition === '+1'
          ? 'next'
          : 'back'
    setTimeout(() => {
      this.shadowRoot?.querySelector(
        `#favorite-section-${sectionId}-item-${item.id} .action-${action} > button`,
      )?.focus()
    }, 10)
  }

  handleLinkClick(e: Event, fname: string | undefined, dnmaName: string | undefined): void {
    document.dispatchEvent(new CustomEvent('favorite-event', {
      detail: {
        event: e,
        fname,
        ...(() => {
          return dnmaName ? { SERVICE: dnmaName } : {}
        })(),
      },
      bubbles: true,
      composed: true,
    }))
  }

  manageTemplate(): TemplateResult | typeof nothing {
    return this.data && this.data.some(({ canDelete, canMove }) => canDelete || canMove)
      ? html`
          <div class="actions">
            <button
              type="button"
              class="btn-secondary small"
              @click="${() => this.toggleManage()}"
            >
              ${!this.isManage ? msg(str`Gérer`) : msg(str`Annuler`)}
              ${getIcon(!this.isManage ? faGear : faTimes)}
            </button>
            ${
              this.isManage
                ? html`
                    <button
                      type="button"
                      class="btn-secondary small"
                      @click="${() => this.toggleManage(true)}"
                    >
                      ${msg(str`Enregistrer`)}${getIcon(faFloppyDisk)}
                    </button>
                  `
                : nothing
            }
          </div>
        `
      : nothing
  }

  itemTemplate(section: FavoriteSection, item: ServiceAndServiceResource, index: number): TemplateResult {
    const { id, canDelete, canMove } = section
    const actionTemplate: TemplateResult | typeof nothing = this.isManage
      ? html`
          <div class="actions">
            ${
              canDelete
                ? html`
                    <div class="action-delete">
                      <button
                        type="button"
                        aria-label="${msg(str`Supprimer le favori`)} - ${item.name}"
                        @click="${() => this.deleteItem(id, item, index)}"
                      >
                        ${getIcon(faTimes)}
                      </button>
                    </div>
                  `
                : nothing
            }
            ${
              canMove
                ? html`
                    <div class="action-back">
                      <button
                        type="button"
                        aria-label="${msg(str`Réordonner vers la gauche`)} - ${item.name}"
                        @click="${() => this.moveItem(id, item, '-1')}"
                      >
                        ${getIcon(faArrowLeft)}
                      </button>
                    </div>
                    <div class="grow-1"></div>
                    <div class="action-next">
                      <button
                        type="button"
                        aria-label="${msg(str`Réordonner vers la droite`)} - ${item.name}"
                        @click="${() => this.moveItem(id, item, '+1')}"
                      >
                        ${getIcon(faArrowRight)}
                      </button>
                    </div>
                  `
                : nothing
            }
          </div>
        `
      : nothing

    const { className } = getCategory(item.category) ?? {}

    return html`
      <li>
        <div
          id="favorite-section-${section.id}-item-${item.id}"
          class="favorite ${className}"
        >
          ${actionTemplate}
          ${getSvgIconService(item.iconUrl)}
          <a
            href="${item.link.href}"
            target="${item.link.target ?? nothing}"
            rel="${item.link.rel ?? nothing}"
            ?inert="${this.isManage}"
            @click="${(e: Event) => this.handleLinkClick(e, item.fname, item.dnmaName)}"
          >
            ${item.name}
          </a>
        </div>
      </li>
    `
  }

  skeletonTemplate(): TemplateResult {
    return html`
        <div class="favorite-layout">
          <div class="header">
            <h2>${msg(str`Vos favoris`)}</h2>
          </div>
          <ul inert>
            ${
              map(
                range(this.loadingSections ?? 1),
                () => html`
                    <li>
                      <div class="header">
                        <span class="skeleton"></span>
                      </div>
                      <ul>
                        ${
                          map(
                            range(this.loadingSectionsItems ?? 4),
                            () => html`<li class="skeleton"></li>`,
                          )
                        }
                      </ul>
                    </li>
                  `,
              )
            }
          </ul>
        </div>
      `
  }

  render(): TemplateResult {
    if (this.loading)
      return this.skeletonTemplate()

    return html`
      <div class="favorite-layout">
        <div class="header">
          <h2>${msg(str`Vos favoris`)}</h2>
          ${
            !(this.data ?? []).some(section => section.loading)
              ? this.manageTemplate()
              : nothing
          }
        </div>
        <ul>
          ${
            repeat(
              !this.isManage ? this.data ?? [] : this.tmpData ?? [],
              section => section.id,
              section => html`
                  <li id="${section.id}">
                    <div class="header">
                      <span>${section.name}</span>
                    </div>
                    ${
                      section.loading
                        ? html`
                            <ul inert>
                              ${
                                map(
                                  range(section.loadingItems ?? 4),
                                  () => html`<li class="skeleton"></li>`,
                                )
                              }
                            </ul>
                          `
                        : section.items.length > 0
                          ? html`
                              <ul>
                                ${
                                  repeat(
                                    section.items,
                                    item => item.id,
                                    (item, index) => this.itemTemplate(section, item, index),
                                  )
                                }
                              </ul>
                            `
                          : html`
                              <div
                                id="favorite-section-${section.id}-empty"
                                tabindex="-1"
                                class="empty"
                              >
                                <span class="text">
                                  ${msg(str`Vous n'avez`)}
                                  <span class="large">
                                    ${section.emptyText}
                                  </span>
                                </span>
                              </div>
                            `
                    }
                  </li>
                `,
            )
          }
        </ul>
      </div>
    `
  }

  static styles = css`${unsafeCSS(styles)}`
}

const tagName = componentName('favorite-layout')

if (!customElements.get(tagName)) {
  customElements.define(tagName, ReciaFavoriteLayout)
}

declare global {
  interface HTMLElementTagNameMap {
    [tagName]: ReciaFavoriteLayout
  }
}
